<template>
  <div class="container-fluid">
    <!-- Filter Section -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <tech-multi-ac 
                  v-model="selectedTechnicians"
                  label="Technicians"
                  placeholder="Select technician to add..."
                />
              </div>
              <div class="col-md-3 ">
                <label class="form-label">Date Range Preset</label>
                <select 
                  class="form-select form-select-sm" 
                  v-model="datePreset"
                  @change="applyDatePreset"
                >
                  <option value="no-date-range">No Date Range</option>
                  <option value="">Custom Range</option>
                  <option value="today">Today</option>
                  <option value="this-week">This Week</option>
                  <option value="last-week">Last Week</option>
                  <option value="next-week">Next Week</option>
                  <option value="this-and-last-week">This &amp; Last Week</option>
                  <option value="this-and-next-week">This &amp; Next Week</option>
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-year">This Year</option>
                  <option value="last-year">Last Year</option>
                </select>
              </div>
              <div class="col-md-2">
                <label class="form-label">Start Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.startDate"
                  :disabled="datePreset === 'no-date-range'"
                />
              </div>
              <div class="col-md-2">
                <label class="form-label">End Date</label>
                <input 
                  type="date" 
                  class="form-control form-control-sm" 
                  v-model="filters.endDate"
                  :disabled="datePreset === 'no-date-range'"
                />
              </div>
              <div class="col-md-2 align-items-start">
                <label class="form-label d-block">&nbsp;</label>
                <button 
                  class="btn btn-sm btn-outline-primary w-100" 
                  @click="loadAssignments"
                  :disabled="selectedTechnicians.length === 0 || loading"
                >
                  <i class="fa fa-play me-2"></i>
                  {{ loading ? 'Loading...' : 'Load Assignments' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2">Loading assignments...</p>
    </div>

    <!-- Report Content -->
    <div v-else-if="ranReport">
      <!-- No Assignments -->
      <div v-if="Object.keys(groupedByDate).length === 0" class="alert alert-info">
        <i class="fa fa-info-circle me-2"></i>
        No assignments found for the selected criteria.
      </div>

      <!-- Grouped by Date Sections -->
      <div v-else>
        <div 
          v-for="(dateGroup, date) in groupedByDate" 
          :key="date"
          class="mb-4"
        >
          <!-- Date Header -->
          <div class="card border-0 shadow-sm mb-2">
            <div class="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2">
              <h6 class="mb-0">
                <i class="fa fa-calendar me-2"></i>
                {{ formatDateHeader(String(date)) }}
              </h6>
              <span class="badge bg-light text-dark">
                {{ dateGroup.totalHours.toFixed(1) }} hours scheduled
              </span>
            </div>
          </div>

          <!-- Assignments Table for this Date -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-0">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 24%;">Assignment</th>
                    <th style="width: 14%;">Client</th>
                    <th style="width: 14%;">Work Order</th>
                    <th style="width: 12%;">Technician</th>
                    <th style="width: 8%; text-align: right;">Hours</th>
                    <th style="width: 14%;">Start By</th>
                    <th style="width: 14%; text-align: center;">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="assignment in dateGroup.assignments" 
                    :key="assignment.Id"
                    class="assignment-row"
                  >
                    <td>
                      <a href="#" @click.prevent="openAssignment(assignment.Id)" class="text-decoration-none">
                        <i class="fa fa-tasks me-2 text-success"></i>
                        {{ assignment.WorkAssignment || 'Untitled Task' }}
                      </a>
                    </td>
                    <td>
                      <span class="text-muted">
                        {{ getClientName(assignment) || 'N/A' }}
                      </span>
                    </td>
                    <td>
                      <a href="#" @click.prevent="openJob(assignment.RequestId)" class="text-decoration-none text-muted">
                        <i class="fa fa-briefcase me-1"></i>
                        #{{ assignment.RequestId }}
                        <span v-if="getJobName(assignment)"> - {{ getJobName(assignment) }}</span>
                      </a>
                    </td>
                    <td>
                      <span class="badge bg-info">
                        {{ getTechnicianName(assignment.AssignedTo) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <strong>{{ (assignment.EstimatedTime || 0).toFixed(1) }}</strong>
                    </td>
                    <td>
                      <div class="d-flex align-items-center gap-1">
                        <input 
                          type="date" 
                          class="form-control form-control-sm"
                          :value="getAssignmentDate(assignment)"
                          @change="rescheduleAssignment(assignment, ($event.target as HTMLInputElement).value)"
                          :disabled="reschedulingId === assignment.Id"
                          style="max-width: 160px;"
                        />
                        <span v-if="reschedulingId === assignment.Id" class="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                    </td>
                    <td class="text-center">
                      <button 
                        class="btn btn-sm btn-success"
                        @click="startClockForAssignment(assignment)"
                        title="Start Clock"
                      >
                        <i class="fa fa-play me-1"></i>Start Clock
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Unscheduled Assignments -->
        <div v-if="unscheduledAssignments.length > 0" class="mb-4">
          <div class="card border-0 shadow-sm mb-2">
            <div class="card-header bg-warning text-dark d-flex justify-content-between align-items-center py-2">
              <h6 class="mb-0">
                <i class="fa fa-exclamation-triangle me-2"></i>
                Unscheduled Assignments
              </h6>
              <span class="badge bg-dark text-white">
                {{ unscheduledAssignments.length }} assignments
              </span>
            </div>
          </div>

          <div class="card border-0 shadow-sm mb-3">
            <div class="card-body p-0">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th style="width: 24%;">Assignment</th>
                    <th style="width: 14%;">Client</th>
                    <th style="width: 14%;">Work Order</th>
                    <th style="width: 12%;">Technician</th>
                    <th style="width: 8%; text-align: right;">Hours</th>
                    <th style="width: 14%;">Schedule For</th>
                    <th style="width: 14%; text-align: center;">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="assignment in unscheduledAssignments" 
                    :key="assignment.Id"
                    class="assignment-row"
                  >
                    <td>
                      <a href="#" @click.prevent="openAssignment(assignment.Id)" class="text-decoration-none">
                        <i class="fa fa-tasks me-2 text-muted"></i>
                        {{ assignment.WorkAssignment || 'Untitled Task' }}
                      </a>
                    </td>
                    <td>
                      <span class="text-muted">
                        {{ getClientName(assignment) || 'N/A' }}
                      </span>
                    </td>
                    <td>
                      <a href="#" @click.prevent="openJob(assignment.RequestId)" class="text-decoration-none text-muted">
                        <i class="fa fa-briefcase me-1"></i>
                        #{{ assignment.RequestId }}
                      </a>
                    </td>
                    <td>
                      <span class="badge bg-info">
                        {{ getTechnicianName(assignment.AssignedTo) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <strong>{{ (assignment.EstimatedTime || 0).toFixed(1) }}</strong>
                    </td>
                    <td>
                      <div class="d-flex align-items-center gap-1">
                        <input 
                          type="date" 
                          class="form-control form-control-sm"
                          @change="rescheduleAssignment(assignment, ($event.target as HTMLInputElement).value)"
                          :disabled="reschedulingId === assignment.Id"
                          style="max-width: 160px;"
                        />
                        <span v-if="reschedulingId === assignment.Id" class="spinner-border spinner-border-sm text-primary"></span>
                      </div>
                    </td>
                    <td class="text-center">
                      <button 
                        class="btn btn-sm btn-success"
                        @click="startClockForAssignment(assignment)"
                        title="Start Clock"
                      >
                        <i class="fa fa-play me-1"></i>Start Clock
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue';
import ApiService from '@/services/ApiService';
import Emitter from '@/services/Emitter';
import Toast from '@/services/Toast';
import Helper, { DateFormat } from '@/services/Helper';
import { TimeModelFilter } from '@/models/TaskModel';
import { RequestFilter } from '@/models/RequestModel';
import TechMultiAc from '@/components/Techs/TechMultiAc.vue';
import { useAppStore } from '@/stores/AppStore';
import { useTechStore } from '@/stores/TechStore';

const props = defineProps<{
  selectedTechIds: number[];
}>();

const appStore = useAppStore();
const techStore = useTechStore();

const filters = ref({
  startDate: '' as string,
  endDate: '' as string
});

const selectedTechnicians = ref<number[]>([]);

const datePreset = ref('no-date-range');
const loading = ref(false);
const ranReport = ref(false);
const assignments = ref<any[]>([]);
const jobs = ref<any[]>([]);
const reschedulingId = ref<number | null>(null);

// Set default tech to current user on mount
onMounted(async () => {
  await techStore.refresh();
  const userId = appStore.account.user?.UserId;
  if (userId) {
    selectedTechnicians.value = [userId];
  }
  applyDatePreset();
});

// Get technician name by ID using TechStore
const getTechnicianName = (techId: number | null | undefined): string => {
  if (!techId) return 'Unassigned';
  const tech = techStore.list[techId];
  return tech?.TechnicianName || 'Unassigned';
};

// Get client name from assignment's contact data
const getClientName = (assignment: any): string => {
  return assignment?.Contact?.Company || '';
};

// Apply date preset
const applyDatePreset = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  let startDate: Date, endDate: Date;

  switch (datePreset.value) {
    case 'today':
      startDate = new Date(today);
      endDate = new Date(today);
      break;
    case 'this-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'last-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff - 7);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'next-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff + 7);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      break;
    }
    case 'this-and-last-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff - 7);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 13);
      break;
    }
    case 'this-and-next-week': {
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      startDate = new Date(today);
      startDate.setDate(today.getDate() - diff);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 13);
      break;
    }
    case 'this-month':
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'last-month':
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'this-year':
      startDate = new Date(today.getFullYear(), 0, 1);
      endDate = new Date(today.getFullYear(), 11, 31);
      break;
    case 'last-year':
      startDate = new Date(today.getFullYear() - 1, 0, 1);
      endDate = new Date(today.getFullYear() - 1, 11, 31);
      break;
    case 'no-date-range':
      filters.value.startDate = '';
      filters.value.endDate = '';
      return;
    default:
      return;
  }

  filters.value.startDate = startDate.toISOString().split('T')[0];
  filters.value.endDate = endDate.toISOString().split('T')[0];
};

// Group assignments by CompleteByDate, sorted chronologically
const groupedByDate = computed(() => {
  const grouped: Record<string, { totalHours: number; assignments: any[] }> = {};
  
  assignments.value.forEach(assignment => {
    if (!assignment.CompleteByDate) return;
    
    const dateKey = new Date(assignment.CompleteByDate).toISOString().split('T')[0];
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = { totalHours: 0, assignments: [] };
    }
    
    const hours = assignment.EstimatedTime || 0;
    grouped[dateKey].assignments.push(assignment);
    grouped[dateKey].totalHours += hours;
  });
  
  // Sort by date
  const sorted: Record<string, { totalHours: number; assignments: any[] }> = {};
  Object.keys(grouped).sort().forEach(key => {
    sorted[key] = grouped[key];
  });
  
  return sorted;
});

// Unscheduled assignments (no CompleteByDate)
const unscheduledAssignments = computed(() => {
  return assignments.value.filter(a => !a.CompleteByDate);
});

// Get job name from assignment's request data
const getJobName = (assignment: any): string => {
  return assignment?.Request?.RequestDetailedDescription || assignment?.Request?.RequestTitle || '';
};

// Format date header
const formatDateHeader = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const dateOnly = dateStr;
  const todayOnly = today.toISOString().split('T')[0];
  const tomorrowOnly = tomorrow.toISOString().split('T')[0];
  
  if (dateOnly === todayOnly) {
    return 'Today - ' + date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } else if (dateOnly === tomorrowOnly) {
    return 'Tomorrow - ' + date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  } else {
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
};

// Get assignment's CompleteByDate as yyyy-MM-dd string
const getAssignmentDate = (assignment: any): string => {
  if (!assignment.CompleteByDate) return '';
  return new Date(assignment.CompleteByDate).toISOString().split('T')[0];
};

// Reschedule assignment to a new date
const rescheduleAssignment = async (assignment: any, newDate: string) => {
  if (!newDate) return;
  
  reschedulingId.value = assignment.Id;
  try {
    // Build a minimal save payload with the updated CompleteByDate
    const payload = { ...assignment };
    payload.CompleteByDate = newDate + 'T00:00:00';
    
    await ApiService.saveTime(payload, true);
    
    // Update the local record
    assignment.CompleteByDate = newDate + 'T00:00:00';
    
    Toast.success(`Assignment rescheduled to ${new Date(newDate + 'T00:00:00').toLocaleDateString()}`);
    
    // Auto-refresh the report to reflect the new grouping
    await loadAssignments();
  } catch (error) {
    console.error('Error rescheduling assignment:', error);
  } finally {
    reschedulingId.value = null;
  }
};

// Start clock for assignment
const startClockForAssignment = (assignment: any) => {
  if (!assignment?.Id) return;
  Emitter.emit('new:clock', { taskId: assignment.Id });
};

// Open job modal
const openJob = (jobId: number) => {
  Emitter.emit('edit:job', jobId);
};

// Open assignment modal
const openAssignment = (assignmentId: number) => {
  Emitter.emit('open:taskForm', { id: assignmentId });
};

// Load assignments
const loadAssignments = async () => {
  if (selectedTechnicians.value.length === 0) return;
  
  loading.value = true;
  ranReport.value = true;
  
  try {
    const filter = new TimeModelFilter();
    filter.TechId = selectedTechnicians.value;
    filter.TimeType = 'Assignments'; // Only unworked assignments
    filter.IncludeContact = true;
    filter.IncludeRequest = true; // Include job/request data

    // TimeModelFilter defaults StartDate/EndDate to today; clear them
    // so "No Date Range" and custom ranges can leave either side open.
    delete (filter as any).StartDate;
    delete (filter as any).EndDate;

    if (filters.value.startDate) {
      filter.StartDate = new Date(filters.value.startDate + 'T00:00:00');
    }
    if (filters.value.endDate) {
      filter.EndDate = new Date(filters.value.endDate + 'T23:59:59');
    }
    filter.Size = 9999;
    
    const response = await ApiService.loadTimes(filter, true);
    
    // Filter to only include records where Type is not 'work'
    const allRecords = response.Data || [];
    assignments.value = allRecords.filter((record: any) => {
      return !record.Type || record.Type.toLowerCase() !== 'work';
    });
    
    // No need to load jobs separately - they're included in the assignment response via IncludeRequest
    
  } catch (error) {
    console.error('Error loading assignments:', error);
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.assignment-row:hover {
  background-color: #f8f9fa;
}
</style>
